// id: only needed for checkboxes
// schemeId: only needed to be send to backend

import {
    Box,
    Button,
    Dialog,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
    BlueDropDownIcon,
    CrossIcon,
    DialogCloseIcon,
    FilterIcon,
    GreyDropDown,
    SearchIcon,
    YellowWarning,
} from "../../../../assets/SvgIcons";
import {
    useEffect,
    useState
} from "react";
import CustomTable from "../../common/customTable";
import {
    navigationData as NavigationData,
    Order,
    PaginationObj,
    SortingObj,
    TableHeaderData
} from "../../types";
import CustomFilter from "../../common/CustomFilter";
import { useHistory } from "react-router";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    AmcFilter,
    AppliedHoldingsFilters,
    GetHoldingsDataResponseBody,
    RiskFilter
} from "../../../../redux-store/types/investorProfile";
import {
    getAmcFilters,
    getHoldingsData,
    getRiskFilters,
    setAppliedHoldingsFilters,
    setHoldingsActiveCategory,
    setSelectedAllHoldingsInfo,
    setSelectedHoldingsInfo
} from "../../../../redux-store/actions/investorProfile";
import { useDebounce } from "../../../../utils/useDebounce";
import { whiteSpaceOnlyRegex } from "../../../../utils/regex";
import HoldingsTable from "./HoldingsTable";
import {
    FetchHoldingsData,
    HoldingsTableData
} from "./types";
import { HELDAWAY_PORTFOLIO_STATUS, MAX_SCHEMES_SELECTED, USER_ROLES } from "../../../../utils/constant";
import { off, title } from "process";
import { showError } from "../../../../redux-store/actions/auth";
import { ToWords } from "to-words";
import { isModuleAllowed } from "../../../../utils/utilityFunctions";

const transactDropdownItems: Array<{ title: string; path: string; }> = [
    {
        title: "Invest More",
        path: 'invest-more'
    },
    {
        title: "Redeem",
        path: 'redeem'
    },
    {
        title: "Switch",
        path: 'switch'
    },
    {
        title: "STP",
        path: 'stp'
    },
    {
        title: "SWP",
        path: 'swp'
    }
]

const categoriesArray: string[] = [
    "All",
    "Equity",
    "Debt",
    "Hybrid",
    "Others"
]

const tableHeaderData: TableHeaderData[] = [
    {
        id: "schemeName",
        label: "SCHEME",
        align: "left",
        isSortable: true
    },
    {
        id: "units",
        label: "UNITS",
        align: "center",
        isSortable: true
    },
    {
        id: "investedValue",
        label: "INVESTED VALUE",
        align: "center",
        isSortable: true
    },
    {
        id: "marketValue",
        label: "MARKET VALUE",
        align: "center",
        isSortable: true
    },
    {
        id: "amount",
        label: "UNREALISED GAIN/LOSS (%)",
        align: "center",
        isSortable: true
    },
    {
        id: "irr",
        label: "ABSOLUTE RETURN %",
        align: "center",
        isSortable: true
    },
]

export default function Holdings(props: NavigationData): JSX.Element {
    const theme = useTheme()

    const toWords = new ToWords();

    const history = useHistory();
    const { pathname: pathName } = history.location

    const dispatch = useDispatch()

    const [transactDropdownEl, setTransactDropdownEl] = useState<null | HTMLElement>(null);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const [isSelectSchemeFirstDialogVisible, setIsSelectSchemeFirstDialogVisible] = useState<boolean>(false)
    const [amcFilterData, setAmcFilterData] = useState<AmcFilter[]>([])
    const [riskFilterData, setRiskFilterData] = useState<RiskFilter[]>([])
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false)
    const [searchText, setSearchText] = useState<string>("")
    const [isHoldingsDataLoading, setHoldingsDataLoading] = useState<boolean>(false)
    const [holdingsData, setHoldingsData] = useState<HoldingsTableData[]>([])
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "schemeName"
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [isFiveSchemesDialogVisible, setFiveSchemesDialogVisible] = useState<boolean>(false)

    const {
        appliedHoldingsFilters,
        holdingsActiveCategory,
        selectedHoldingsInfo,
        selectAllHoldingsInfo
    } = useSelector((store: RootStateType) => store.investorProfile);

    const { iwappRmId = "", investorClientId = "" } = useSelector((store: RootStateType) => store.id)

    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData)

    const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const { selectedPortfolio, isPortfolioHeldaway } = useSelector((state: RootStateType) => state.selectedPortfiolio)
    const { label: selectedPortfolioLabel = "", value: selectedPortfolioValue = "" } = selectedPortfolio

    const debounceSearchText = useDebounce(searchText, 500);

    const isTransactDropdownOpen = Boolean(transactDropdownEl);

    const clientCode = ![USER_ROLES.INVESTOR].includes(role) ? props?.clientCode ?? "" : investorClientId;

    const fetchFilterData = async () => {
        try {
            setIsFiltersLoading(true)

            const appliedAmcFilters = appliedHoldingsFilters
                .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
                .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

            const appliedRiskFilters = appliedHoldingsFilters
                .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
                .map((obj: AppliedHoldingsFilters) => `${obj.riskId}`)

            const amcFilterData = await (dispatch(getAmcFilters()) as unknown as Promise<AmcFilter[]>)
            const modifiedAmcFilterData = amcFilterData.map((obj: AmcFilter) => ({
                ...obj,
                companyId: `${obj.companyId}`,
                checked: appliedAmcFilters.includes(`${obj.companyId}`)
            }))
            setAmcFilterData(modifiedAmcFilterData)

            const riskFilterData = await (dispatch(getRiskFilters()) as unknown as Promise<RiskFilter[]>)
            const modifiedRiskFilterData = riskFilterData.map((obj: RiskFilter) => ({
                ...obj,
                riskId: `${obj.risk_id}`,
                checked: appliedRiskFilters.includes(`${obj.risk_id}`)
            }))

            setRiskFilterData(modifiedRiskFilterData)
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsFiltersLoading(false)
        }
    }

    useEffect(() => {
        fetchFilterData()
    }, [])

    const fetchHoldingsData = async ({
        currentCategory = "All",
        selectedAmcIds = [],
        selectedRiskTypes = [],
        searchText = "",
        sortBy = "investedValue",
        sortOrder = "desc",
        currentPage = 1,
        rowsPerPage = 5,
        isCategoryChange = false
    }: FetchHoldingsData) => {
        try {
            setHoldingsDataLoading(true)

            const payload = {
                rmId: `${investorRmId}`,
                clientCode: clientCode,
                portfolioId: selectedPortfolioValue,
                filters: {
                    category: currentCategory,
                    issuerId: selectedAmcIds,
                    riskType: selectedRiskTypes,
                    searchItem: searchText
                },
                sortItem: sortBy,
                order: sortOrder,
                currentPage,
                rowsPerPage
            }

            if ((debounceSearchText.trim().length === 0 || debounceSearchText.trim().length >= 5)
                && !whiteSpaceOnlyRegex.test(debounceSearchText)) {

                const { result, totalLength, message, isSuccess } = await (dispatch(getHoldingsData(payload)) as unknown as Promise<GetHoldingsDataResponseBody>)

                if (isSuccess) {
                    setHoldingsData(result.map((obj) => ({
                        ...obj,
                        schemeName: obj?.schemeName ?? "-",
                        schemeLogo: obj?.schemeLogo ?? "",
                        units: obj?.units ?? 0,
                        investedValue: obj?.investedValue ?? 0,
                        marketValue: obj?.marketValue ?? 0,
                        percentage: {
                            amount: obj?.percentage?.amount ?? 0,
                            percentage: obj?.percentage?.percentage ?? 0
                        },
                        irr: obj?.irr ?? 0,
                        schemeId: obj?.schemeId ?? "",
                        categoryName: obj?.categoryName ?? "",
                        companyName: obj?.companyName ?? "",
                        folioNo: obj?.folioNo ?? "",
                        checked: isCategoryChange ? false : selectAllHoldingsInfo.filter((selecetObj) => selecetObj.id === obj.id).length > 0,
                        id: obj?.id ?? ''
                    })))

                    setTablePaginationObj(prevState => ({
                        ...prevState,
                        totalRows: totalLength
                    }))
                } else {
                    console.log(message)
                }
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setHoldingsDataLoading(false)
        }

    }

    useEffect(() => {
        const amcFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const riskFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskName}`.toLowerCase())


        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }, [debounceSearchText])


    const handleTransactDropdownOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setTransactDropdownEl(event.currentTarget);
    };

    const handleTransactDropdownClose = () => {
        setTransactDropdownEl(null);
    };

    const handleTransactDropdownItemsClick = (currentItem: { path: string, title: string }) => {
        if (isPortfolioHeldaway) {
            dispatch(showError(HELDAWAY_PORTFOLIO_STATUS))
            setTransactDropdownEl(null);
            return
        }

        const modifiedSelectedHoldingsInfo = Array.from(
            new Map(selectedHoldingsInfo.map(item => [item.checkedId, item])).values()
        )

        const modifiedSelectAllHoldingsInfo = Array.from(
            new Map(selectAllHoldingsInfo.map(item => [item.id, item])).values()
        )

        dispatch(setSelectedHoldingsInfo(modifiedSelectedHoldingsInfo))
        dispatch(setSelectedAllHoldingsInfo(modifiedSelectAllHoldingsInfo))

        const selectedHoldingsIds = modifiedSelectedHoldingsInfo.map(obj => obj.schemeId)
        const schemeDetails = modifiedSelectAllHoldingsInfo?.map((obj) => ({
            scheme_id: obj?.schemeId ?? "-",
            isReinvestment: obj?.isReinvest ?? "-"
        }))

        const data = modifiedSelectAllHoldingsInfo.map(item => ({
            ...item,
            folioId: item.folioNo,
            scheme: item.schemeId,
            id: item.id,
            marketValue: item.marketValue,
            investedValue: item.investedValue,
            isReinvestment: item?.isReinvest
        }))

        setTransactDropdownEl(null);
        // history.push(`${pathName}/${currentItem.path}`, {
        //     selectedSchemes: data,
        //     clientCode: clientCode,
        //     schemeDetails,
        //     ...(currentItem.title === "Invest More" ?
        //         {
        //             folio: data.map((obj) => ({
        //                 folioId: obj.folioId,
        //                 schemeId: obj.scheme,
        //                 id: obj.id,
        //                 marketValue: obj.marketValue,
        //                 investedValue: obj.investedValue
        //             })),
        //         } : {
        //             itemData: selectAllHoldingsInfo[0]
        //         })
        // });
        history.push(`${pathName}/${currentItem.path}`, {
            selectedSchemes: data,
            clientCode: clientCode,
            schemeDetails,
            folio: data.map((obj) => ({
                folioId: obj.folioId,
                schemeId: obj.scheme,
                id: obj.id,
                marketValue: obj.marketValue,
                investedValue: obj.investedValue
            }))
            // ...(["Invest More", "Redeem", "SWP"].includes(currentItem.title) ?
            //     {
            //         folio: data.map((obj) => ({
            //             folioId: obj.folioId,
            //             schemeId: obj.scheme,
            //             id: obj.id,
            //             marketValue: obj.marketValue,
            //             investedValue: obj.investedValue
            //         })),
            //     } : {
            //         itemData: selectAllHoldingsInfo[0]
            //     })
        });
    }

    const handleOpenFilterDrawer = () => {
        const appliedAmcFilters = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const appliedRiskFilters = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskId}`)

        setAmcFilterData((prevState: AmcFilter[]) => prevState.map((obj: AmcFilter) => ({
            ...obj,
            checked: appliedAmcFilters.includes(`${obj.companyId}`)
        })))

        setRiskFilterData((prevState: RiskFilter[]) => prevState.map((obj: RiskFilter) => ({
            ...obj,
            checked: appliedRiskFilters.includes(`${obj.risk_id}`)
        })))

        setFilterDrawerOpen(true)
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const handleCheckboxClick = (schemeId: string, checked: boolean, id: string) => {
        if (selectAllHoldingsInfo.length === MAX_SCHEMES_SELECTED && checked) {
            setFiveSchemesDialogVisible(true)
            return;
        }

        const currentTimestamp = Date.now()

        if (!selectedHoldingsInfo.find(obj => obj.checkedId === id) && checked) {
            const selectedSchemeObj = holdingsData.find(obj => obj.id === id)

            dispatch(setSelectedHoldingsInfo([
                ...selectedHoldingsInfo, {
                    checkedId: id,
                    schemeId,
                    folioNo: selectedSchemeObj?.folioNo ?? "",
                    uniqueId: `${currentTimestamp}-${selectedSchemeObj?.isinCode ?? ""}-${selectedSchemeObj?.folioNo ?? ""}`
                }]))
            dispatch(setSelectedAllHoldingsInfo([
                ...selectAllHoldingsInfo,
                ...holdingsData
                    .filter(obj => obj.id === id)
                    .map(obj => ({
                        ...obj,
                        uniqueId: `${currentTimestamp}-${obj?.isinCode ?? ""}-${obj?.folioNo ?? ""}`
                    }))
            ]))
        } else if (selectedHoldingsInfo.find(obj => obj.checkedId === id) && !checked) {
            const modifiedSelectedHoldingsInfo = selectedHoldingsInfo.filter(({ checkedId }) => checkedId !== id)
            const modifiedSelectedAllHoldingsInfo = selectAllHoldingsInfo.filter(({ id: checkedId }) => checkedId !== id)

            dispatch(setSelectedHoldingsInfo([...modifiedSelectedHoldingsInfo]))
            dispatch(setSelectedAllHoldingsInfo([...modifiedSelectedAllHoldingsInfo]))
        }

        setHoldingsData(prevState => prevState.map(
            item => item.id === id ? { ...item, checked } : item)
        )
    }

    const handleAmcFilterChange = (value: string, checked: boolean) => {
        setAmcFilterData(prevState => prevState.map(
            item => item.companyId === value ? { ...item, checked } : item)
        )
    }

    const handleRiskFilterChange = (value: string, checked: boolean) => {
        setRiskFilterData(prevState => prevState.map(
            item => item.risk_id === value ? { ...item, checked } : item)
        )
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    const handleCategoryChange = (currentCategory: string) => {
        dispatch(setHoldingsActiveCategory(currentCategory))
        dispatch(setSelectedHoldingsInfo([]))
        dispatch(setSelectedAllHoldingsInfo([]))

        const amcFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const riskFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskName}`.toLowerCase())

        setTablePaginationObj(prevState => ({ ...prevState, currentPage: 0 }))

        fetchHoldingsData({
            currentCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            isCategoryChange: true
        })
    }

    const handleApplyFilters = () => {
        setFilterDrawerOpen(false)

        const appliedAmcFilters = amcFilterData
            .filter(obj => obj.checked)
            .map(obj => ({
                label: obj.companyName,
                value: obj.companyId,
                filterType: "amc",
                filterTypeValue: `amc-${obj.companyId}`,
                companyId: obj.companyId,
                companyName: obj.companyName
            }))

        const appliedRiskFilters = riskFilterData
            .filter(obj => obj.checked)
            .map(obj => ({
                label: obj.risk_type,
                value: obj.risk_id,
                filterType: "risk",
                filterTypeValue: `risk-${obj.risk_id}`,
                riskId: obj.risk_id,
                riskName: obj.risk_type
            }))

        dispatch(setAppliedHoldingsFilters([...appliedAmcFilters, ...appliedRiskFilters]))

        const amcFiltersPayload = appliedAmcFilters
            .map(obj => `${obj.companyId}`)

        const riskFiltersPayload = appliedRiskFilters
            .map(obj => `${obj.riskName}`.toLowerCase())

        setTablePaginationObj(prevState => ({ ...prevState, currentPage: 0 }))

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleFiltersClearAll = () => {
        setAmcFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))

        setRiskFilterData(prevState => prevState.map(item => ({ ...item, checked: false })))

        dispatch(setAppliedHoldingsFilters([]))

        setTablePaginationObj(prevState => ({ ...prevState, currentPage: 0 }))

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: [],
            selectedRiskTypes: [],
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleFilterLeavesClick = async (
        currentFilterType: string,
        currentFilterTypeValue: string,
        currentValue: string
    ) => {
        const modifiedAppliedHoldingsFilters = appliedHoldingsFilters.filter(obj => obj.filterTypeValue !== currentFilterTypeValue)

        dispatch(setAppliedHoldingsFilters(modifiedAppliedHoldingsFilters))

        const amcFiltersPayload = modifiedAppliedHoldingsFilters
            .filter(obj => obj.filterType.toLowerCase() === 'amc')
            .map(obj => `${obj.companyId}`)

        const riskFiltersPayload = modifiedAppliedHoldingsFilters
            .filter(obj => obj.filterType.toLowerCase() === 'risk')
            .map(obj => `${obj.riskName}`.toLowerCase())

        setAmcFilterData(prevState => prevState
            .map(obj => currentFilterType === "amc" && obj.companyId === currentValue ? { ...obj, checked: false } : obj)
        )

        setRiskFilterData(prevState => prevState
            .map(obj => currentFilterType === "risk" && obj.risk_id === currentValue ? { ...obj, checked: false } : obj)
        )

        setTablePaginationObj(prevState => ({ ...prevState, currentPage: 0 }))

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleSort = (orderBy: string, order: Order) => {
        setTableSortingObj({
            order,
            orderBy
        })

        const amcFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const riskFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskName}`.toLowerCase())

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: orderBy,
            sortOrder: order,
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        const amcFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const riskFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskName}`.toLowerCase())

        setTablePaginationObj(prevState => ({ ...prevState, currentPage: 0 }))

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            rowsPerPage: Number(event.target.value)
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        const amcFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "amc")
            .map((obj: AppliedHoldingsFilters) => `${obj.companyId}`)

        const riskFiltersPayload = appliedHoldingsFilters
            .filter((obj: AppliedHoldingsFilters) => obj.filterType.toLowerCase() === "risk")
            .map((obj: AppliedHoldingsFilters) => `${obj.riskName}`.toLowerCase())

        fetchHoldingsData({
            currentCategory: holdingsActiveCategory,
            selectedAmcIds: amcFiltersPayload,
            selectedRiskTypes: riskFiltersPayload,
            searchText: debounceSearchText.trim(),
            sortBy: tableSortingObj.orderBy,
            sortOrder: tableSortingObj.order,
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage
        })
    }

    const handleFiveSchemesDialogClose = () => {
        setFiveSchemesDialogVisible(false)
    }

    const isFilterApplied = amcFilterData.some(obj => obj.checked) || riskFilterData.some(obj => obj.checked)

    const investMoreBtn = () => {

        // setTransactDropdownEl(null);
        // history.push(`${pathName}/invest-more`, {
        //     selectedSchemes: selectedHoldingsIds,
        //     clientCode,
        //     folio: selectedHoldingsIds?.map((schemeId) => holdingsData.filter((obj) => obj.schemeId === schemeId).map(obj => {
        //         return {
        //             folioId: obj.folioNo,
        //             schemeId: obj.schemeId
        //         }
        //     })[0])
        // });

        if (isPortfolioHeldaway) {
            dispatch(showError(HELDAWAY_PORTFOLIO_STATUS))
            return
        }

        const selectedHoldingsIds = selectedHoldingsInfo.map(obj => obj.schemeId)
        const schemeDetails = selectAllHoldingsInfo?.map((obj) => {
            return {
                scheme_id: obj?.schemeId ?? "-",
                isReinvestment: obj?.isReinvest ?? "-"
            }
        })

        const data = selectAllHoldingsInfo.map(item => ({
            folioId: item.folioNo,
            scheme: item.schemeId,
            id: item.id,
            marketValue: item.marketValue,
            investedValue: item.investedValue,
            isReinvestment: item?.isReinvest,
        }))

        setTransactDropdownEl(null);
        history.push(`${pathName}/invest-more`, {
            selectedSchemes: data,
            schemeDetails,
            clientCode: clientCode,
            folio: data.map((obj) => ({
                folioId: obj.folioId,
                schemeId: obj.scheme,
                id: obj.id,
                marketValue: obj.marketValue,
                investedValue: obj.investedValue
            })),
        });
    }

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    borderRadius: "25px",
                    padding: "35px"
                })}
            >
                <Grid
                    container
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                    }}
                >
                    <Grid
                        item
                        xs={3}
                    >
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                            })}
                        >
                            Investments
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        container
                        xs={5}
                        sx={{
                            columnGap: "25px",
                            justifyContent: "flex-end",
                            alignItems: "center"
                        }}
                    >
                        <Button
                            variant="text"
                            startIcon={<FilterIcon />}
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                padding: "5px",
                                ...theme.typography.body_500,
                                // "&:hover": {
                                //     backgroundColor: theme.palette.grey[50]
                                // }
                            })}
                            TouchRippleProps={{
                                style: {
                                    color: theme.palette.grey[300]
                                }
                            }}
                            onClick={handleOpenFilterDrawer}
                        >
                            Filters
                        </Button>
                        <Button
                            id="transact-button"
                            aria-haspopup={selectAllHoldingsInfo.length > 0 ? 'menu' : 'dialog'}
                            aria-controls={
                                selectAllHoldingsInfo.length > 0 && isTransactDropdownOpen
                                    ? 'transact-menu'
                                    : undefined
                            }
                            aria-expanded={
                                selectAllHoldingsInfo.length > 0 ? isTransactDropdownOpen : undefined
                            }
                            aria-describedby={
                                selectAllHoldingsInfo.length === 0 ? 'transact-button-hint' : undefined
                            }
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                if (selectAllHoldingsInfo.length > 0) {
                                    handleTransactDropdownOpen(event)
                                } else {
                                    setIsSelectSchemeFirstDialogVisible(true)
                                }
                            }}
                            endIcon={selectAllHoldingsInfo.length === 0 ? <GreyDropDown /> : <BlueDropDownIcon />}
                            sx={(theme: Theme) => ({
                                borderRadius: "5px",
                                border: `1px solid ${selectAllHoldingsInfo.length > 0 ? theme.palette.primary[500] : theme.palette.text.secondary}`,
                                backgroundColor: theme.palette.common.white,
                                color: selectAllHoldingsInfo.length > 0 ? theme.palette.primary[500] : theme.palette.text.secondary,
                                maxWidth: "202px",
                                width: "100%",
                                height: "40px",
                                ...theme.typography.body_500,
                                "&.Mui-disabled": {
                                    borderColor: theme.palette.text.secondary,
                                    color: theme.palette.text.secondary,
                                    cursor: "not-allowed",
                                    pointerEvents: "auto"
                                }
                            })}
                            disabled={
                                !isModuleAllowed({ value: 'MFOrders', compareArr: permissions, objKey: "permissonCode" }) &&
                                !isModuleAllowed({ value: 'OrderRecommend', compareArr: permissions, objKey: "permissonCode" })
                            }
                        >
                            Transact
                        </Button>
                        {/* Screen-reader-only hint that explains the no-selection
                            state. Referenced by the button via aria-describedby
                            when no holdings are selected. */}
                        <Box component="span" id="transact-button-hint" sx={visuallyHidden}>
                            Select one or more holdings first to choose a transaction type.
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{
                    marginTop: "20px",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "5px"

                }}>
                    <TextField
                        autoFocus
                        fullWidth
                        inputProps={{
                            'aria-describedby': 'holdings-search-helper',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                        onMouseDown={handleClearSearch}
                                        aria-label="Clear Search"
                                    >
                                        <DialogCloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: (theme: Theme) => ({
                                borderRadius: '8px',
                                height: '50px',
                                border: `1px solid ${theme.palette.text.secondary}B2`,
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                    color: theme.palette.text.primary,
                                    '::placeholder': {
                                        ...theme.typography.subtitle_400,
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                    },
                                },
                            }),
                        }}
                        value={searchText}
                        placeholder="Search"
                        onChange={handleSearchTextChange}
                    />
                    <Typography
                        id="holdings-search-helper"
                        variant="overline_600"
                        sx={(theme: Theme) => ({
                            color: theme.palette.grey[800],
                            marginTop: "5px"
                        })}
                    >
                        Minimum 5 characters required
                    </Typography>
                </Box>
                <Box
                    sx={{
                        marginTop: "20px",
                        display: "flex",
                        gap: "10px",
                        padding:'3px',
                        overflow: 'auto'
                    }}
                    role="tablist"
                >
                    {categoriesArray.map((category) => (
                        <Button
                            key={category}
                            id={`holdings-tab-${category}`}
                            sx={(theme: Theme) => ({
                                padding: "3px 15px",
                                borderRadius: "7px",
                                backgroundColor: holdingsActiveCategory === category ? theme.palette.primary[500] : theme.palette.common.white,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                            })}
                            onClick={() => handleCategoryChange(category)}
                            aria-controls="holdings-tabpanel"
                            aria-selected={holdingsActiveCategory === category}
                            role="tab"
                        >
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: holdingsActiveCategory === category ? theme.palette.textDark.primary : theme.palette.text.secondary
                                })}
                            >
                                {category}
                            </Typography>
                        </Button>
                    ))}
                </Box>
                <Box
                    sx={{
                        marginTop: "25px",
                        display: appliedHoldingsFilters.length ? "flex" : "none",
                        gap: "15px",
                        flexWrap: "wrap",
                    }}
                >
                    {appliedHoldingsFilters.map((obj) => (
                        <Button
                            key={obj.filterTypeValue}
                            variant="contained"
                            startIcon={<CrossIcon />}
                            onClick={() => handleFilterLeavesClick(
                                obj.filterType,
                                obj.filterTypeValue,
                                obj.value
                            )}
                            sx={(theme: Theme) => ({
                                borderRadius: "61px",
                                backgroundColor: theme.palette.grey[900],
                                padding: "7px 16px",
                                color: theme.palette.text.primary,
                                whiteSpace: "nowrap",
                                boxShadow: "none",
                                ...theme.typography.subtitle_400,
                                "& .MuiButton-startIcon": {
                                    paddingTop: "2px"
                                },
                                "&:hover": {
                                    backgroundColor: theme.palette.grey[800],
                                    boxShadow: "none"
                                },
                                "&.Mui-disabled": {
                                    opacity: 0.5
                                }
                            })}
                            disabled={isHoldingsDataLoading}
                        >
                            {obj.label}
                        </Button>
                    ))}
                </Box>
                <Box
                    id="holdings-tabpanel"
                    role="tabpanel"
                    aria-labelledby={`holdings-tab-${holdingsActiveCategory}`}
                    sx={{ marginTop: "25px" }}
                >
                    <CustomTable
                        isLoading={isHoldingsDataLoading}
                        tableBody={
                            <HoldingsTable
                                data={holdingsData}
                                onCheckboxClick={handleCheckboxClick}
                            />
                        }
                        tableData={holdingsData}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th:first-of-type': {
                                paddingLeft: "0px"
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        tableHeaderData={tableHeaderData}
                        handleSort={handleSort}
                        tableDataCount={tablePaginationObj.totalRows}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        rowsPerPageOptions={[5, 10, 15, 20]}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        currentPage={tablePaginationObj.currentPage}
                        order={tableSortingObj.order}
                        orderBy={tableSortingObj.orderBy}
                        loadingRowsNum={5}
                    />
                </Box>
            </Box >
            <Menu
                id="transact-menu"
                anchorEl={transactDropdownEl}
                open={isTransactDropdownOpen}
                onClose={handleTransactDropdownClose}
                MenuListProps={{
                    'aria-labelledby': 'transact-button',
                    sx: {
                        '& > :not(:last-child)': {
                            marginBottom: '5px',
                        }
                    }
                }}
                slotProps={{
                    paper: {
                        style: {
                            width: '202px',
                            borderRadius: "12px",
                            paddingLeft: "8px",
                            paddingRight: "8px",
                            backgroundColor: theme.palette.common.white
                        }
                    }
                }}
            >
                {transactDropdownItems
                    .map((item): JSX.Element =>
                        <MenuItem
                            key={item.title}
                            onClick={() => handleTransactDropdownItemsClick(item as { path: string; title: string; })}
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                                padding: "8px",
                                ...theme.typography.body_500,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary[50],
                                    borderRadius: "8px",
                                    color: theme.palette.primary[500]
                                },
                            })}
                        >
                            {item.title}
                        </MenuItem>
                    )}
            </Menu>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    amcFilterData={amcFilterData}
                    handleAmcFilterChange={handleAmcFilterChange}
                    showCategoryFilter={false}
                    riskFilterData={riskFilterData}
                    handleRiskFilterChange={handleRiskFilterChange}
                    isFilterApplied={isFilterApplied}
                    defaultExpandedFilter="amc"
                />
            </Drawer>
            <Dialog
                onClose={() => setIsSelectSchemeFirstDialogVisible(false)}
                open={isSelectSchemeFirstDialogVisible}
                fullWidth
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "24px",
                        maxWidth: "533px"
                    },
                })}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary,
                        marginBottom: "12px"
                    })}
                >
                    Select Scheme(s) first!
                </Typography>
                <Typography
                    variant="subtitle_400"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.secondary,
                        marginBottom: "12px"
                    })}
                >
                    Please select the scheme(s) first before you proceed to transact.
                </Typography>
                <Typography
                    variant="subtitle_400"
                    sx={(theme: Theme) => ({
                        color: theme.palette.primary[500],
                        marginBottom: "48px",
                        lineHeight: "20px",
                        fontWeight: 600
                    })}
                >
                    Note
                    <Typography
                        component="span"
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.secondary,
                            lineHeight: "24px"
                        })}
                    >
                        : You’re allowed to select multiple schemes.
                    </Typography>
                </Typography>
                <Button
                    fullWidth
                    onClick={() => setIsSelectSchemeFirstDialogVisible(false)}
                    variant="contained"
                    sx={(theme: Theme) => ({
                        borderRadius: "4px",
                        padding: "8px 20px",
                        ...theme.typography.overline_600,
                        fontSize: "16px"
                    })}
                >
                    Ok
                </Button>
            </Dialog >
            <Dialog
                onClose={handleFiveSchemesDialogClose}
                open={isFiveSchemesDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                {MAX_SCHEMES_SELECTED} schemes at a time!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleFiveSchemesDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                    <Typography
                        variant="subtitle_400"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.secondary,
                        })}
                    >
                        You can  {![USER_ROLES.INVESTOR].includes(role) ? "recommend" : "proceed"} up to {`${toWords.convert(MAX_SCHEMES_SELECTED)}`.toLowerCase()} schemes simultaneously!
                    </Typography>
                </Box>
            </Dialog>
        </>
    )
}

